import { useCallback, useLayoutEffect, useRef } from "react";
import { trpc } from "@lango/api";
import { Voice } from "@lango/api/src/queries/config/types";
import { captureError } from "@/services/sentry";

const AUDIO_ID = "use-audio-handler";
const SOURCE_ID = "use-audio-source-handler";

let Queue: any[] = [];
const TIMEOUT = 1000;
const QUEUE_LIMIT = 5;
let isPlaying = false;

export const useAudio = () => {
  const onEnd = useRef<any>();
  const { mutateAsync: textToAudio } = trpc.textToAudio.useMutation({
    trpc: {
      context: {
        skipBatch: true,
      },
    },
  });

  useLayoutEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const ref = document.getElementById(AUDIO_ID);
    if (!ref) {
      const audio = document.createElement("audio");
      audio.id = AUDIO_ID;
      const source = document.createElement("source");
      source.id = SOURCE_ID;
      audio.appendChild(source);
      document.body.appendChild(audio);
    }
  }, []);

  const getAudioRef = useCallback(() => {
    const ref = document.getElementById(AUDIO_ID);
    if (!ref) {
      throw new Error("Audio element not found");
    }
    return ref;
  }, []);

  const getSourceRef = useCallback(() => {
    const ref = document.getElementById(SOURCE_ID);
    if (!ref) {
      throw new Error("Source element not found");
    }
    return ref;
  }, []);

  const onEnded = useCallback(() => {
    isPlaying = false;
    if (onEnd.current) {
      onEnd.current();
      onEnd.current = null;
    }
    if (Queue.length > 0) {
      const { data, type, onEnd } = Queue.shift();
      setTimeout(() => {
        data.then((src: string) => {
          playAudio(src, type, onEnd);
        });
      }, TIMEOUT);
    } else {
      isPlaying = false;
    }
  }, [onEnd]);

  const playAudio = useCallback(
    async (data: string, type: string, onEndCallback?: () => void) => {
      const audio = getAudioRef() as HTMLAudioElement;
      const source = getSourceRef() as HTMLSourceElement;
      onEnd.current = onEndCallback;
      source.type = type;
      audio.onended = onEnded;
      source.src = `data:audio/mpeg;base64,${data}`;
      audio.load();
      audio.play();
    },
    [getAudioRef, getSourceRef, onEnd]
  );

  const playText = useCallback(
    async (text: string, voice: Voice) => {
      if (isPlaying) {
        if (Queue.length > QUEUE_LIMIT) {
          return;
        }
        Queue.push({
          data: textToAudio({ text, ...voice }),
          type: "audio/mpeg",
        });
        return;
      }
      isPlaying = true;
      const data = await textToAudio({ text, ...voice });
      if (!data) {
        captureError(new Error("No data returned from textToAudio"));
        return;
      }
      playAudio(data, "audio/mpeg");
    },
    [isPlaying, textToAudio]
  );

  return {
    playText,
  };
};
